import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useVerifiedStatus } from '../hooks/useVerifiedStatus';
import { useResponsiveLayout } from '../hooks/useResponsiveLayout';
import AppMenu from './AppMenu';
import LanguageSwitcher from './LanguageSwitcher';

export default function AppHeader() {
  const { user, isAuthenticated, logout } = useAuth();
  const { verified } = useVerifiedStatus();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const responsive = useResponsiveLayout();
  const [showMenu, setShowMenu] = useState(false);
  const [showAvatarMenu, setShowAvatarMenu] = useState(false);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);

  // Fetch pending approvals count
  useEffect(() => {
    if (!isAuthenticated || !user || !user.role?.includes('admin')) {
      return;
    }

    const fetchPendingCount = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/users/pending/count`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setPendingApprovalsCount(data.count || 0);
        }
      } catch (err) {
        console.error('Failed to fetch pending approvals count:', err);
      }
    };

    fetchPendingCount();

    // Refresh count every 30 seconds
    const interval = setInterval(fetchPendingCount, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, user]);

  if (!isAuthenticated || !user) {
    return null;
  }

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isAdmin = user.role === 'admin' || user.role === 'superadmin';
  const isSuperadmin = user.role === 'superadmin';
  // BJOECHIA-only desk cleanup: these 5 items move to /archived for this
  // one account specifically, per explicit request -- every other
  // admin/superadmin keeps seeing them in the main nav as before.
  const isBjoechia = (user?.wellis_id || '').toUpperCase() === 'WELLIS-BJOECHIA';

  // Mobile Header
  if (responsive.isMobile) {
    return (
      <header className="fixed top-0 left-0 right-0 h-14 flex items-center z-40 border-b shadow-md" style={{ backgroundColor: '#0F2915', borderBottomColor: 'rgba(212, 175, 55, 0.2)' }}>
        <div className="w-full px-4 flex justify-between items-center">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="hover:opacity-80 transition-opacity p-2 -ml-2"
            style={{ color: '#D4AF37' }}
            aria-label="Menu"
            title="Menu"
          >
            ☰
          </button>

          <Link to="/" className="hover:opacity-80 transition-opacity flex items-center gap-2" title="Wellis Home">
            <img
              src="/images/wellis-logo.png"
              alt="Wellis"
              className="h-10 w-auto"
            />
            <span className="text-headline-sm font-bold uppercase tracking-widest" style={{ color: '#D4AF37' }}>
              WELLIS
            </span>
          </Link>

          {isAdmin ? (
            <button
              onClick={() => navigate('/admin')}
              className="hover:opacity-80 transition-opacity p-2 relative"
              style={{ color: '#D4AF37' }}
              title={`${pendingApprovalsCount} pending approvals`}
              aria-label="Notifications"
            >
              🔔
              {pendingApprovalsCount > 0 && (
                <span className="absolute top-1 right-1 bg-error text-on-error text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
                  {pendingApprovalsCount}
                </span>
              )}
            </button>
          ) : (
            <LanguageSwitcher />
          )}
        </div>

        {/* Mobile Menu Dropdown */}
        {showMenu && (
          <div className="absolute top-full left-0 right-0 border-b shadow-lg" style={{ backgroundColor: 'rgba(15, 41, 21, 0.98)', borderBottomColor: 'rgba(212, 175, 55, 0.2)' }}>
            <AppMenu
              isAdmin={isAdmin}
              isSuperadmin={isSuperadmin}
              isBjoechia={isBjoechia}
              isMobile={true}
              onNavigate={() => setShowMenu(false)}
            />
          </div>
        )}
      </header>
    );
  }

  // Desktop Header
  return (
    <header className="fixed top-0 left-0 right-0 min-h-16 py-2 flex items-center z-40 border-b shadow-md" style={{ backgroundColor: '#0F2915', borderBottomColor: 'rgba(212, 175, 55, 0.2)' }}>
      <div className="max-w-container-max mx-auto px-margin-mobile w-full flex flex-nowrap justify-between items-center gap-x-4">
        {/* Logo */}
        <Link to="/dashboard" className="hover:opacity-80 transition-opacity flex items-center gap-2 flex-shrink-0" title="Wellis Dashboard">
          <img
            src="/images/wellis-logo.png"
            alt="Wellis"
            className="h-12 w-auto"
          />
          <span className="text-headline-lg font-bold uppercase tracking-widest whitespace-nowrap" style={{ color: '#D4AF37' }}>
            WELLIS
          </span>
        </Link>

        {/* Desktop Navigation -- nowrap + horizontal scroll so the fixed
            header's height stays constant as nav items grow, instead of
            wrapping to a 2nd line and silently overlapping page content
            that assumes a fixed single-line header height (pt-24) */}
        <div className="hidden md:flex flex-nowrap items-center gap-x-5 overflow-x-auto min-w-0" style={{ scrollbarWidth: 'thin' }}>
          <Link
            to="/dashboard"
            className="text-body-md hover:opacity-80 transition-opacity flex-shrink-0 whitespace-nowrap"
            style={{ color: '#D4AF37' }}
          >
            {t('nav.dashboard')}
          </Link>
          <Link
            to="/genealogy"
            className="text-body-md hover:opacity-80 transition-opacity flex-shrink-0 whitespace-nowrap"
            style={{ color: '#D4AF37' }}
          >
            {t('nav.genealogy')}
          </Link>
          {!isBjoechia && (
            <Link
              to="/members"
              className="text-body-md hover:opacity-80 transition-opacity flex-shrink-0 whitespace-nowrap"
              style={{ color: '#D4AF37' }}
            >
              {t('nav.members')}
            </Link>
          )}
          {verified && (
            <Link
              to="/invite"
              className="text-body-md hover:opacity-80 transition-opacity flex-shrink-0 whitespace-nowrap"
              style={{ color: '#D4AF37' }}
            >
              {t('nav.invite')}
            </Link>
          )}
          {!isBjoechia && (
            <Link
              to="/packages"
              className="text-body-md hover:opacity-80 transition-opacity flex-shrink-0 whitespace-nowrap"
              style={{ color: '#D4AF37' }}
            >
              {t('nav.packages')}
            </Link>
          )}
          <Link
            to="/marketing-plan"
            className="text-body-md hover:opacity-80 transition-opacity flex-shrink-0 whitespace-nowrap"
            style={{ color: '#D4AF37' }}
          >
            {t('nav.marketingPlan')}
          </Link>
          <Link
            to="/guides"
            className="text-body-md hover:opacity-80 transition-opacity flex-shrink-0 whitespace-nowrap"
            style={{ color: '#D4AF37' }}
          >
            {t('nav.guides')}
          </Link>
          {isAdmin && (
            <>
              <Link
                to="/admin"
                className="text-body-md hover:opacity-80 transition-opacity flex-shrink-0 whitespace-nowrap"
                style={{ color: '#D4AF37' }}
              >
                {t('nav.approvals')}
              </Link>
              <Link
                to="/admin-dashboard"
                className="text-body-md hover:opacity-80 transition-opacity flex-shrink-0 whitespace-nowrap"
                style={{ color: '#D4AF37' }}
              >
                {t('nav.adminDashboard')}
              </Link>
              <Link
                to="/admin-guide"
                className="text-body-md hover:opacity-80 transition-opacity flex-shrink-0 whitespace-nowrap"
                style={{ color: '#D4AF37' }}
              >
                {t('nav.adminGuide')}
              </Link>
              <Link
                to="/welcome-email"
                className="text-body-md hover:opacity-80 transition-opacity flex-shrink-0 whitespace-nowrap"
                style={{ color: '#D4AF37' }}
              >
                {t('nav.welcomeEmail')}
              </Link>
              {!isBjoechia && (
                <Link
                  to="/member-rewards"
                  className="text-body-md hover:opacity-80 transition-opacity flex-shrink-0 whitespace-nowrap"
                  style={{ color: '#D4AF37' }}
                >
                  {t('nav.memberRewards')}
                </Link>
              )}
              <Link
                to="/sales-team"
                className="text-body-md hover:opacity-80 transition-opacity flex-shrink-0 whitespace-nowrap"
                style={{ color: '#D4AF37' }}
              >
                {t('nav.salesTeam')}
              </Link>
              <Link
                to="/reports"
                className="text-body-md hover:opacity-80 transition-opacity flex-shrink-0 whitespace-nowrap"
                style={{ color: '#D4AF37' }}
              >
                {t('nav.salesReports')}
              </Link>
              <Link
                to="/balances"
                className="text-body-md hover:opacity-80 transition-opacity flex-shrink-0 whitespace-nowrap"
                style={{ color: '#D4AF37' }}
              >
                {t('nav.balances')}
              </Link>
              {!isBjoechia && (
                <Link
                  to="/signup-wizard"
                  className="text-body-md hover:opacity-80 transition-opacity flex-shrink-0 whitespace-nowrap"
                  style={{ color: '#D4AF37' }}
                >
                  {t('nav.signupWizard')}
                </Link>
              )}
            </>
          )}
          {(user.role === 'sales-team' || isAdmin) && (
            <>
              {!isBjoechia && (
                <Link
                  to="/signup-internal"
                  className="text-body-md hover:opacity-80 transition-opacity flex-shrink-0 whitespace-nowrap"
                  style={{ color: '#D4AF37' }}
                >
                  {t('nav.signupInternal')}
                </Link>
              )}
              <Link
                to="/rnr-credit-requests"
                className="text-body-md hover:opacity-80 transition-opacity flex-shrink-0 whitespace-nowrap"
                style={{ color: '#D4AF37' }}
              >
                {t('nav.rnrCreditRequests')}
              </Link>
            </>
          )}
          {isSuperadmin && (
            <>
              <Link
                to="/ranking"
                className="text-body-md hover:opacity-80 transition-opacity flex-shrink-0 whitespace-nowrap"
                style={{ color: '#D4AF37' }}
              >
                {t('nav.ranking')}
              </Link>
              <Link
                to="/audit-logs"
                className="text-body-md hover:opacity-80 transition-opacity flex-shrink-0 whitespace-nowrap"
                style={{ color: '#D4AF37' }}
              >
                {t('nav.auditLogs')}
              </Link>
              <Link
                to="/admin-credit-topups"
                className="text-body-md hover:opacity-80 transition-opacity flex-shrink-0 whitespace-nowrap"
                style={{ color: '#D4AF37' }}
              >
                {t('nav.creditTopups')}
              </Link>
              <Link
                to="/archived"
                className="text-body-md hover:opacity-80 transition-opacity flex-shrink-0 whitespace-nowrap"
                style={{ color: '#D4AF37' }}
              >
                {t('nav.archived')}
              </Link>
            </>
          )}
        </div>

        {/* Right Section: Notifications + Avatar */}
        <div className="flex items-center gap-4 flex-shrink-0">
          <LanguageSwitcher />
          {/* Notification Badge */}
          {isAdmin && (
            <button
              onClick={() => navigate('/admin')}
              className="hover:opacity-80 transition-opacity relative p-2"
              style={{ color: '#D4AF37' }}
              title={`${pendingApprovalsCount} pending approvals`}
            >
              🔔
              {pendingApprovalsCount > 0 && (
                <span className="absolute top-0 right-0 bg-error text-on-error text-[10px] rounded-full w-5 h-5 flex items-center justify-center font-bold">
                  {pendingApprovalsCount}
                </span>
              )}
            </button>
          )}

          {/* Avatar Menu */}
          <div className="relative">
            <button
              onClick={() => setShowAvatarMenu(!showAvatarMenu)}
              className="hover:opacity-80 transition-opacity text-2xl p-1"
              style={{ color: '#D4AF37' }}
              title="User menu"
            >
              👤
            </button>

            {showAvatarMenu && (
              <div className="absolute top-full right-0 mt-2 bg-surface border border-outline rounded-lg shadow-lg min-w-max z-50">
                <div className="px-4 py-3 border-b border-outline">
                  <div className="font-medium text-on-surface">{user.email?.split('@')[0] || 'User'}</div>
                  <div className="text-body-xs text-on-surface/60 capitalize">{user.role}</div>
                </div>

                <button
                  onClick={() => {
                    navigate('/edit-profile');
                    setShowAvatarMenu(false);
                  }}
                  className="w-full text-left px-4 py-2 text-body-sm text-on-surface hover:bg-primary/10 transition-colors"
                >
                  {t('nav.profile')}
                </button>

                <button
                  className="w-full text-left px-4 py-2 text-body-sm text-on-surface hover:bg-primary/10 transition-colors"
                >
                  {t('nav.notifications')}
                </button>

                <button
                  className="w-full text-left px-4 py-2 text-body-sm text-on-surface hover:bg-primary/10 transition-colors"
                >
                  {t('nav.security')}
                </button>

                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-2 text-body-sm text-error hover:bg-error/10 transition-colors border-t border-outline"
                >
                  {t('nav.logout')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
